import React, { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { MODULOS } from '../lib/utils';
import { RESULTADOS_NACIONALES } from '../lib/resultados-nacionales';
import ProgresoParvularia from './ProgresoParvularia';
import { isParvularia } from '../lib/parvularia-data';
import { useIsParvularia } from '../context/TesterModalityContext';
import { useAuth } from '../context/AuthContext';
import EstrategiaPortafolioSelector, { ESTRATEGIA_LABELS, ESTRATEGIA_LABELS_FULL, ESTRATEGIA_BADGES } from '../components/EstrategiaPortafolioSelector';
import MotorEstrategicoView from '../components/MotorEstrategicoView';
import { toast } from 'sonner';
import {
    getAnalisisEstrategico,
    getEstadisticasModulo,
    getSimulacion,
    getRecomendaciones,
} from '../lib/estrategia-data';
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import {
    Target, Clock, Zap, TrendingUp, AlertTriangle, CheckCircle2,
    Link2, Shield, Brain, ChevronRight, ChevronDown, Lightbulb, BarChart3,
    ArrowRight, Flame, Layers, Check, X as XIcon, MessageSquare, Compass,
} from 'lucide-react';

// --- Color helpers ---
const SEMAFORO_STYLES = {
    red: 'bg-red-500',
    amber: 'bg-amber-400',
    green: 'bg-emerald-500',
};
const SEMAFORO_BG = {
    red: 'bg-red-50 border-red-200',
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-emerald-50 border-emerald-200',
};
const SEMAFORO_TEXT = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    green: 'text-emerald-700',
};
const BAR_COLORS = {
    Insatisfactorio: '#ef4444',
    Básico: '#f59e0b',
    Competente: '#10b981',
    Destacado: '#6366f1',
};
const SCENARIO_COLORS = {
    slate: { bar: '#64748b', bg: 'bg-slate-50 border-slate-200', text: 'text-slate-700' },
    blue: { bar: '#3b82f6', bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
    emerald: { bar: '#10b981', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
};
const RECO_ICONS = {
    'target': Target,
    'link': Link2,
    'check-circle': CheckCircle2,
    'clock': Clock,
};

// --- Subcomponents ---

function ModuloCard({ modulo, indicadores, stats }) {
    const activo = modulo.estado === 'activo';
    return (
        <Card className={cn(
            'transition-all',
            activo ? 'border-blue-200 bg-white shadow-sm' : 'border-slate-200 bg-slate-50/60 opacity-75'
        )} data-testid={`modulo-card-${modulo.id}`}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-base">{modulo.nombre}</CardTitle>
                    <Badge variant={activo ? 'default' : 'outline'} className={cn('text-[10px]', activo ? 'bg-blue-600' : '')}>
                        {activo ? 'Activo' : 'Próximamente'}
                    </Badge>
                </div>
                <CardDescription className="text-xs">{modulo.titulo}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {stats ? (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Nivel estimado</p>
                                <p className="mt-0.5 text-sm font-bold text-slate-800">{stats.nivelEstimado}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Tiempo est.</p>
                                <p className="mt-0.5 text-sm font-bold text-slate-800">{stats.totalHoras}h</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Desgaste</p>
                                <div className="mt-0.5 flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <Flame key={i} className={cn('h-3.5 w-3.5', i <= Math.round(stats.desgastePromedio) ? 'text-orange-500' : 'text-slate-200')} />
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Impacto</p>
                                <Badge variant="outline" className={cn('mt-0.5 text-[10px]',
                                    stats.impactoEstrategico === 'Alto' ? 'border-red-300 text-red-600' :
                                    stats.impactoEstrategico === 'Medio' ? 'border-amber-300 text-amber-600' :
                                    'border-emerald-300 text-emerald-600'
                                )}>
                                    {stats.impactoEstrategico}
                                </Badge>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-red-500 font-semibold">{stats.criticos} criticos</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-[10px] text-emerald-500 font-semibold">{stats.fortalezas} fortalezas</span>
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-slate-400 text-center py-4">Datos disponibles próximamente</p>
                )}
            </CardContent>
        </Card>
    );
}

function HeatMapRow({ indicador, maxCritico }) {
    const intensity = indicador.pctCritico / maxCritico;
    const hue = 120 - intensity * 120; // 120=green -> 0=red
    return (
        <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
            <div
                className="h-8 w-8 rounded flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                style={{ backgroundColor: `hsl(${hue}, 70%, 50%)` }}
                title={`${indicador.pctCritico}% en niveles críticos`}
            >
                {Math.round(indicador.pctCritico)}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{indicador.nombre}</p>
                <p className="text-[10px] text-slate-400">{indicador.tarea}</p>
            </div>
            <div className={cn('h-2.5 w-2.5 rounded-full flex-shrink-0', SEMAFORO_STYLES[indicador.semaforo])} />
            <span className={cn('text-[10px] font-semibold w-12 text-right', SEMAFORO_TEXT[indicador.semaforo])}>
                {indicador.dificultad}
            </span>
        </div>
    );
}

function NivelDiagnostico({ titulo, color, data }) {
    if (!data) return null;
    return (
        <div className={cn('rounded-lg border p-3 space-y-1.5', color)}>
            <p className="text-xs font-bold text-slate-700">{titulo}</p>
            <p className="text-[10px] text-slate-500 italic">{data.significa}</p>
            {data.logra?.length > 0 && (
                <div className="space-y-0.5">
                    {data.logra.map((t, i) => (
                        <div key={i} className="flex items-start gap-1">
                            <Check className="h-3 w-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-[10px] text-slate-600">{t}</span>
                        </div>
                    ))}
                </div>
            )}
            {data.noLogra?.length > 0 && (
                <div className="space-y-0.5">
                    {data.noLogra.map((t, i) => (
                        <div key={i} className="flex items-start gap-1">
                            <XIcon className="h-3 w-3 text-red-400 flex-shrink-0 mt-0.5" />
                            <span className="text-[10px] text-slate-600">{t}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function IndicadorDetail({ indicador }) {
    const [expanded, setExpanded] = useState(false);
    const diag = RESULTADOS_NACIONALES[indicador.id]?.diagnostico;
    const barData = [
        { nivel: 'Insatisf.', pct: indicador.pctInsatisfactorio, full: 'Insatisfactorio' },
        { nivel: 'Básico', pct: indicador.pctBasico, full: 'Básico' },
        { nivel: 'Competente', pct: indicador.pctCompetente, full: 'Competente' },
        { nivel: 'Destacado', pct: indicador.pctDestacado, full: 'Destacado' },
    ];
    return (
        <Card className={cn('border transition-all', SEMAFORO_BG[indicador.semaforo])} data-testid={`indicador-detail-${indicador.id}`}>
            <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="text-sm font-semibold text-slate-800">{indicador.nombre}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{indicador.tarea} | {indicador.totalDocentes.toLocaleString()} docentes evaluados</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className={cn('h-3 w-3 rounded-full', SEMAFORO_STYLES[indicador.semaforo])} />
                        <span className={cn('text-xs font-bold', SEMAFORO_TEXT[indicador.semaforo])}>{indicador.dificultad}</span>
                    </div>
                </div>

                {/* Frase síntesis */}
                {diag?.fraseSintesis && (
                    <div className="flex items-start gap-2 bg-white/70 rounded-lg p-2.5 border border-slate-200/60">
                        <MessageSquare className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-slate-700 italic leading-relaxed">{diag.fraseSintesis}</p>
                    </div>
                )}

                {/* Mini bar chart */}
                <div className="flex gap-1 h-16 items-end">
                    {barData.map(d => (
                        <div key={d.nivel} className="flex-1 flex flex-col items-center justify-end h-full">
                            <span className="text-[9px] font-mono font-bold text-slate-600 mb-0.5">{d.pct}%</span>
                            <div
                                className="w-full rounded-t"
                                style={{
                                    height: `${Math.max(d.pct, 2)}%`,
                                    backgroundColor: BAR_COLORS[d.full],
                                }}
                            />
                            <span className="text-[8px] text-slate-400 mt-0.5 leading-none">{d.nivel}</span>
                        </div>
                    ))}
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-200/60">
                    <div className="text-center">
                        <Clock className="h-3.5 w-3.5 mx-auto text-slate-400" />
                        <p className="text-xs font-bold text-slate-700 mt-0.5">{indicador.horas.min}-{indicador.horas.max}h</p>
                        <p className="text-[9px] text-slate-400">Tiempo est.</p>
                    </div>
                    <div className="text-center">
                        <Flame className="h-3.5 w-3.5 mx-auto text-orange-400" />
                        <p className="text-xs font-bold text-slate-700 mt-0.5">{indicador.desgaste}/5</p>
                        <p className="text-[9px] text-slate-400">Desgaste</p>
                    </div>
                    <div className="text-center">
                        <Zap className="h-3.5 w-3.5 mx-auto text-blue-400" />
                        <p className="text-xs font-bold text-slate-700 mt-0.5">{indicador.impactoTransversal}</p>
                        <p className="text-[9px] text-slate-400">Impacto</p>
                    </div>
                </div>

                {/* Relations */}
                {indicador.impactaA.length > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-blue-600">
                        <Link2 className="h-3 w-3" />
                        <span>Impacta: {indicador.impactaA.map(id => {
                            const parts = id.split('-');
                            return `Ind. ${parts[1]}.${parts[2]}`;
                        }).join(', ')}</span>
                    </div>
                )}

                {/* Expand button for diagnostic */}
                {diag && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="w-full flex items-center justify-center gap-1.5 pt-1.5 border-t border-slate-200/60 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                        data-testid={`expand-diagnostico-${indicador.id}`}
                    >
                        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        {expanded ? 'Ocultar diagnóstico' : 'Ver diagnóstico pedagógico'}
                    </button>
                )}

                {/* Expanded diagnostic */}
                {expanded && diag && (
                    <div className="space-y-3 animate-fade-in">
                        {/* Levels breakdown */}
                        <div className="grid gap-2">
                            <NivelDiagnostico titulo="Nivel Básico" color="border-amber-200 bg-amber-50/50" data={diag.nivelBasico} />
                            <NivelDiagnostico titulo="Nivel Competente" color="border-emerald-200 bg-emerald-50/50" data={diag.nivelCompetente} />
                            <NivelDiagnostico titulo="Nivel Destacado" color="border-indigo-200 bg-indigo-50/50" data={diag.nivelDestacado} />
                        </div>

                        {/* Interpretation */}
                        {diag.interpretacion && (
                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Interpretación del sistema</p>
                                <p className="text-[11px] text-slate-700 leading-relaxed">{diag.interpretacion}</p>
                            </div>
                        )}

                        {/* Strategy */}
                        {diag.estrategia && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <Target className="h-3.5 w-3.5 text-blue-600" />
                                    <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Estrategia recomendada</p>
                                </div>
                                <p className="text-[11px] text-blue-800 leading-relaxed">{diag.estrategia}</p>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ScenarioCard({ scenario }) {
    const sc = SCENARIO_COLORS[scenario.color];
    const total = scenario.destacado + scenario.competente + scenario.basico + scenario.insatisfactorio;
    return (
        <Card className={cn('border', sc.bg)}>
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <p className={cn('text-sm font-bold', sc.text)}>{scenario.nombre}</p>
                    <span className={cn('text-2xl font-black font-mono', sc.text)}>{scenario.probabilidad}%</span>
                </div>
                <p className="text-[10px] text-slate-500">{scenario.descripcion}</p>
                <div className="flex gap-1 h-4 rounded-full overflow-hidden">
                    {scenario.destacado > 0 && <div className="bg-indigo-500" style={{ width: `${(scenario.destacado / total) * 100}%` }} title={`${scenario.destacado} Destacado`} />}
                    {scenario.competente > 0 && <div className="bg-emerald-500" style={{ width: `${(scenario.competente / total) * 100}%` }} title={`${scenario.competente} Competente`} />}
                    {scenario.basico > 0 && <div className="bg-amber-400" style={{ width: `${(scenario.basico / total) * 100}%` }} title={`${scenario.basico} Básico`} />}
                    {scenario.insatisfactorio > 0 && <div className="bg-red-500" style={{ width: `${(scenario.insatisfactorio / total) * 100}%` }} title={`${scenario.insatisfactorio} Insuf.`} />}
                </div>
                <div className="flex justify-between text-[9px] text-slate-500">
                    <span>{scenario.destacado}D / {scenario.competente}C / {scenario.basico}B / {scenario.insatisfactorio}I</span>
                </div>
            </CardContent>
        </Card>
    );
}

function RecoCard({ reco }) {
    const Icon = RECO_ICONS[reco.icono] || Lightbulb;
    const colorMap = {
        red: 'border-red-200 bg-red-50',
        blue: 'border-blue-200 bg-blue-50',
        green: 'border-emerald-200 bg-emerald-50',
        amber: 'border-amber-200 bg-amber-50',
    };
    const textMap = {
        red: 'text-red-700',
        blue: 'text-blue-700',
        green: 'text-emerald-700',
        amber: 'text-amber-700',
    };
    return (
        <div className={cn('rounded-xl border p-4 space-y-1.5', colorMap[reco.color])}>
            <div className="flex items-center gap-2">
                <Icon className={cn('h-4 w-4', textMap[reco.color])} />
                <p className={cn('text-sm font-bold', textMap[reco.color])}>{reco.titulo}</p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">{reco.mensaje}</p>
        </div>
    );
}

// --- Main Page ---

export default function Progreso() {
    // Fase B Parvularia + Tester QA: dispatcher.
    const { profile } = useAuth();
    const esParvularia = useIsParvularia(profile?.perfil);
    if (esParvularia) {
        return <ProgresoParvularia />;
    }
    return <ProgresoLegacy />;
}

function ProgresoLegacy() {
    const [selectedTab, setSelectedTab] = useState('general');
    const [selectedModulo, setSelectedModulo] = useState('todos');
    const { profile, updateProfile } = useAuth();
    const estrategiaActual = profile?.perfil?.estrategia_portafolio || null;
    const [estrategiaOpen, setEstrategiaOpen] = useState(!estrategiaActual);
    const [savingEstrategia, setSavingEstrategia] = useState(false);

    const handleEstrategiaChange = async (nuevaEstrategia) => {
        if (savingEstrategia) return;
        setSavingEstrategia(true);
        try {
            await updateProfile({
                perfil: {
                    ...(profile?.perfil || {}),
                    estrategia_portafolio: nuevaEstrategia,
                },
            });
            toast.success(`Estrategia actualizada: ${ESTRATEGIA_LABELS_FULL[nuevaEstrategia]}`);
            setEstrategiaOpen(false);
        } catch (e) {
            toast.error('No pude guardar tu estrategia. Intenta de nuevo.');
        } finally {
            setSavingEstrategia(false);
        }
    };

    // All indicators or filtered by module
    const allIndicadores = useMemo(() => getAnalisisEstrategico(), []);
    const indicadores = useMemo(() =>
        selectedModulo === 'todos' ? allIndicadores : allIndicadores.filter(i => i.modulo === selectedModulo),
    [allIndicadores, selectedModulo]);

    // Per-module stats
    const m1Indicadores = useMemo(() => getAnalisisEstrategico('modulo-1'), []);
    const m2Indicadores = useMemo(() => getAnalisisEstrategico('modulo-2'), []);
    const m3Indicadores = useMemo(() => getAnalisisEstrategico('modulo-3'), []);
    const m1Stats = useMemo(() => getEstadisticasModulo(m1Indicadores), [m1Indicadores]);
    const m2Stats = useMemo(() => getEstadisticasModulo(m2Indicadores), [m2Indicadores]);
    const m3Stats = useMemo(() => getEstadisticasModulo(m3Indicadores), [m3Indicadores]);

    const simulacion = useMemo(() => getSimulacion(indicadores), [indicadores]);
    const recomendaciones = useMemo(() => getRecomendaciones(indicadores), [indicadores]);

    // Radar chart data
    const radarData = useMemo(() => indicadores.map(ind => ({
        indicador: ind.numOficial || ind.id.replace('indicador-', ''),
        'Competente+Destacado': Math.round(ind.pctCompetente + ind.pctDestacado),
        'Básico+Insatisf.': Math.round(ind.pctBasico + ind.pctInsatisfactorio),
        fullName: ind.nombre,
    })), [indicadores]);

    const maxCritico = Math.max(...indicadores.map(i => i.pctCritico));

    const sortedByPriority = useMemo(() =>
        [...indicadores].sort((a, b) => (b.pctCritico * (1 + b.impactoTransversal * 0.3)) - (a.pctCritico * (1 + a.impactoTransversal * 0.3))),
    [indicadores]);

    const moduloLabel = selectedModulo === 'todos' ? 'Portafolio Completo' : MODULOS.find(m => m.id === selectedModulo)?.nombre;

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="mapa-logro">
                {/* Header */}
                <div>
                    <div className="flex items-center gap-2">
                        <Brain className="h-6 w-6 text-blue-600" />
                        <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl" data-testid="mapa-title">
                            Mapa de Logro
                        </h1>
                    </div>
                    <p className="mt-1 text-slate-500 text-sm md:text-base">
                        Inteligencia estratégica para abordar tu portafolio eficientemente
                    </p>
                </div>

                {/* Quote */}
                <div className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 p-5 text-white shadow-lg" data-testid="frase-central">
                    <p className="text-base md:text-lg font-medium italic text-center">
                        "No se trata de hacer más. Se trata de hacerlo inteligentemente."
                    </p>
                    <p className="text-center text-blue-200 text-xs mt-1">Portafolio Docente IA - Acontista Educa</p>
                </div>

                {/* Estrategia de Portafolio */}
                <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden" data-testid="estrategia-portafolio-card">
                    <div className="flex items-center justify-between p-5">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-100 to-amber-100 text-rose-700">
                                <Compass className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="font-heading text-base font-bold text-slate-900">Estrategia de Portafolio</h3>
                                <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                    {estrategiaActual ? (
                                        <>
                                            <span>Tu estrategia activa:</span>
                                            <strong className="text-slate-700">{ESTRATEGIA_LABELS[estrategiaActual]}</strong>
                                            {ESTRATEGIA_BADGES[estrategiaActual] && (
                                                <span
                                                    className="inline-flex items-center rounded-md bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                                                    data-testid="estrategia-activa-badge-acontista"
                                                >
                                                    {ESTRATEGIA_BADGES[estrategiaActual]}
                                                </span>
                                            )}
                                            <span className="block w-full mt-1">El sistema conversa contigo desde esta lente.</span>
                                        </>
                                    ) : (
                                        'Define cómo quieres que el sistema te acompañe en el Portafolio.'
                                    )}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEstrategiaOpen((v) => !v)}
                            className="text-xs gap-1"
                            data-testid="toggle-estrategia"
                        >
                            {estrategiaOpen ? 'Cerrar' : (estrategiaActual ? 'Cambiar' : 'Elegir mi estrategia')}
                            {estrategiaOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </Button>
                    </div>
                    {estrategiaOpen && (
                        <div className="border-t border-slate-200 bg-slate-50/40 p-5">
                            <EstrategiaPortafolioSelector
                                value={estrategiaActual}
                                onChange={handleEstrategiaChange}
                            />
                        </div>
                    )}
                </div>

                {/* Module filter */}
                <div className="flex flex-wrap gap-2" data-testid="module-filter">
                    {[
                        { id: 'todos', label: 'Portafolio Completo' },
                        { id: 'modulo-1', label: 'Módulo 1' },
                        { id: 'modulo-2', label: 'Módulo 2' },
                        { id: 'modulo-3', label: 'Módulo 3' },
                    ].map(m => (
                        <Button
                            key={m.id}
                            variant={selectedModulo === m.id ? 'default' : 'outline'}
                            size="sm"
                            className={cn('text-xs', selectedModulo === m.id && 'bg-blue-600 hover:bg-blue-700')}
                            onClick={() => setSelectedModulo(m.id)}
                        >
                            {m.label}
                        </Button>
                    ))}
                </div>

                {/* Tab Navigation */}
                <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-5">
                    <TabsList className="grid w-full grid-cols-4 h-auto">
                        <TabsTrigger value="general" className="text-xs py-2 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <Layers className="h-3.5 w-3.5" /> General
                        </TabsTrigger>
                        <TabsTrigger value="indicadores" className="text-xs py-2 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <BarChart3 className="h-3.5 w-3.5" /> Indicadores
                        </TabsTrigger>
                        <TabsTrigger value="simulacion" className="text-xs py-2 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <TrendingUp className="h-3.5 w-3.5" /> Simulación
                        </TabsTrigger>
                        <TabsTrigger value="estrategia" className="text-xs py-2 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <Target className="h-3.5 w-3.5" /> Estrategia
                        </TabsTrigger>
                    </TabsList>

                    {/* ==================== TAB: GENERAL ==================== */}
                    <TabsContent value="general" className="space-y-6">
                        {/* Module cards */}
                        <div>
                            <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">Vista por Módulo</h2>
                            <div className="grid gap-4 md:grid-cols-3">
                                <ModuloCard modulo={MODULOS[0]} indicadores={m1Indicadores} stats={m1Stats} />
                                <ModuloCard modulo={MODULOS[1]} indicadores={m2Indicadores} stats={m2Stats} />
                                <ModuloCard modulo={MODULOS[2]} indicadores={m3Indicadores} stats={m3Stats} />
                            </div>
                        </div>

                        {/* Radar chart */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="font-heading text-base flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-blue-500" />
                                    Radar de Logro Nacional — {moduloLabel}
                                </CardTitle>
                                <CardDescription className="text-xs">% de docentes en Competente+Destacado vs Básico+Insatisfactorio por indicador ({indicadores.length} indicadores)</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={320}>
                                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                                        <PolarGrid stroke="#e2e8f0" />
                                        <PolarAngleAxis dataKey="indicador" tick={{ fontSize: 11, fill: '#475569' }} />
                                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                                        <Radar name="Competente+Destacado" dataKey="Competente+Destacado" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
                                        <Radar name="Básico+Insatisf." dataKey="Básico+Insatisf." stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 4" />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Tooltip
                                            content={({ payload }) => {
                                                if (!payload?.length) return null;
                                                const d = payload[0].payload;
                                                return (
                                                    <div className="rounded-lg border bg-white p-2.5 shadow-lg text-xs">
                                                        <p className="font-bold text-slate-800 mb-1">{d.fullName}</p>
                                                        <p className="text-blue-600">Comp.+Dest.: {d['Competente+Destacado']}%</p>
                                                        <p className="text-red-500">Bás.+Insuf.: {d['Básico+Insatisf.']}%</p>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        {/* Heat map */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="font-heading text-base flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                    Mapa de Calor: Dificultad Nacional
                                </CardTitle>
                                <CardDescription className="text-xs">
                                    % de docentes en niveles Insatisfactorio + Básico (mayor = más difícil)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="divide-y-0">
                                    {sortedByPriority.map(ind => (
                                        <HeatMapRow key={ind.id} indicador={ind} maxCritico={maxCritico} />
                                    ))}
                                </div>
                                <div className="flex items-center justify-center gap-2 mt-4 text-[10px] text-slate-400">
                                    <div className="flex items-center gap-1"><div className="h-3 w-3 rounded" style={{ backgroundColor: 'hsl(120,70%,50%)' }} /> Fácil</div>
                                    <ArrowRight className="h-3 w-3" />
                                    <div className="flex items-center gap-1"><div className="h-3 w-3 rounded" style={{ backgroundColor: 'hsl(60,70%,50%)' }} /> Media</div>
                                    <ArrowRight className="h-3 w-3" />
                                    <div className="flex items-center gap-1"><div className="h-3 w-3 rounded" style={{ backgroundColor: 'hsl(0,70%,50%)' }} /> Difícil</div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ==================== TAB: INDICADORES ==================== */}
                    <TabsContent value="indicadores" className="space-y-6">
                        <div>
                            <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">Indicadores — {moduloLabel}</h2>
                            <p className="text-xs text-slate-500 mb-4">Ordenados por prioridad estratégica ({indicadores.length} indicadores)</p>
                        </div>

                        {/* National distribution chart */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="font-heading text-base">Distribución Nacional por Indicador</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={Math.max(280, indicadores.length * 36)}>
                                    <BarChart data={indicadores.map(i => ({
                                        name: i.numOficial || i.id,
                                            Insatisfactorio: i.pctInsatisfactorio,
                                            Básico: i.pctBasico,
                                            Competente: i.pctCompetente,
                                            Destacado: i.pctDestacado,
                                    }))} layout="vertical" margin={{ left: 10, right: 10 }}>
                                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={35} />
                                        <Tooltip contentStyle={{ fontSize: 11 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="Insatisfactorio" stackId="a" fill="#ef4444" />
                                        <Bar dataKey="Básico" stackId="a" fill="#f59e0b" />
                                        <Bar dataKey="Competente" stackId="a" fill="#10b981" />
                                        <Bar dataKey="Destacado" stackId="a" fill="#6366f1" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        {/* Indicator detail cards */}
                        <div className="grid gap-4 md:grid-cols-2">
                            {sortedByPriority.map(ind => (
                                <IndicadorDetail key={ind.id} indicador={ind} />
                            ))}
                        </div>
                    </TabsContent>

                    {/* ==================== TAB: SIMULACIÓN ==================== */}
                    <TabsContent value="simulacion" className="space-y-6">
                        <div>
                            <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">Simulación de Resultados</h2>
                            <p className="text-xs text-slate-500 mb-4">Tres escenarios posibles basados en tu nivel de preparación</p>
                        </div>

                        {simulacion && (
                            <div className="grid gap-4 md:grid-cols-3">
                                {simulacion.map(s => (
                                    <ScenarioCard key={s.nombre} scenario={s} />
                                ))}
                            </div>
                        )}

                        {/* Comparative bar chart */}
                        {simulacion && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="font-heading text-base">Comparación de Escenarios</CardTitle>
                                    <CardDescription className="text-xs">Probabilidad de resultado global positivo</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={simulacion.map(s => ({
                                            name: s.nombre,
                                            probabilidad: s.probabilidad,
                                            color: s.color,
                                        }))}>
                                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                                            <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `${v}%`} />
                                            <Bar dataKey="probabilidad" radius={[6, 6, 0, 0]}>
                                                {simulacion.map((s, i) => (
                                                    <Cell key={i} fill={SCENARIO_COLORS[s.color].bar} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        )}

                        {/* Key insight */}
                        <Card className="border-blue-200 bg-blue-50/50">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-blue-800">Dato clave</p>
                                        <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                                            Con preparación estratégica, puedes pasar de un escenario conservador ({simulacion?.[0].probabilidad}%)
                                            a un escenario óptimo ({simulacion?.[1].probabilidad}%), mejorando significativamente tus
                                            probabilidades sin necesitar sobre-esfuerzo en indicadores donde ya tienes buen nivel.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ==================== TAB: ESTRATEGIA ==================== */}
                    <TabsContent value="estrategia" className="space-y-8">
                        {/* Motor Estratégico — capa central de decisiones IA */}
                        <MotorEstrategicoView />

                        <div className="border-t border-slate-100 pt-6">
                            <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">Recomendaciones tácticas adicionales</h2>
                            <p className="text-xs text-slate-500 mb-4">Sugerencias específicas según tu perfil actual</p>
                        </div>

                        {/* Recommendations */}
                        <div className="space-y-3">
                            {recomendaciones.map((r, i) => (
                                <RecoCard key={i} reco={r} />
                            ))}
                        </div>

                        {/* Strategic categories summary */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="font-heading text-base">Clasificación Estratégica</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {[
                                        { cat: 'critico-alto-impacto', label: 'Críticos de Alto Impacto', desc: 'Difíciles + mejoran otros indicadores', color: 'bg-red-500' },
                                        { cat: 'critico', label: 'Críticos', desc: 'Alta dificultad nacional', color: 'bg-orange-500' },
                                        { cat: 'arrastre', label: 'Efecto Arrastre', desc: 'Mejoran otros indicadores al trabajarlos', color: 'bg-blue-500' },
                                        { cat: 'estable', label: 'Estables', desc: 'Dificultad intermedia, mantener nivel', color: 'bg-slate-400' },
                                        { cat: 'fortaleza', label: 'Fortalezas', desc: 'Bajo riesgo, no sobre-invertir', color: 'bg-emerald-500' },
                                    ].map(({ cat, label, desc, color }) => {
                                        const items = indicadores.filter(i => i.categoria === cat);
                                        if (!items.length) return null;
                                        return (
                                            <div key={cat} className="flex items-start gap-3">
                                                <div className={cn('h-3 w-3 rounded-full mt-1 flex-shrink-0', color)} />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-semibold text-slate-800">{label}</p>
                                                        <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
                                                    </div>
                                                    <p className="text-[10px] text-slate-400">{desc}</p>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {items.map(i => (
                                                            <Badge key={i.id} variant="secondary" className="text-[10px]">{i.nombre}</Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Time allocation */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="font-heading text-base flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-blue-500" />
                                    Distribución de Tiempo Recomendada
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {sortedByPriority.map(ind => {
                                        const totalHoras = indicadores.reduce((s, i) => s + i.horas.promedio, 0);
                                        const pct = (ind.horas.promedio / totalHoras) * 100;
                                        return (
                                            <div key={ind.id} className="space-y-0.5">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-700 truncate max-w-[60%]">{ind.nombre}</span>
                                                    <span className="text-slate-500 font-mono">{ind.horas.promedio}h ({Math.round(pct)}%)</span>
                                                </div>
                                                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                                    <div
                                                        className={cn('h-full rounded-full transition-all', SEMAFORO_STYLES[ind.semaforo])}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between text-sm">
                                    <span className="text-slate-600 font-medium">Total estimado</span>
                                    <span className="font-bold text-slate-900 font-mono">
                                        {Math.round(indicadores.reduce((s, i) => s + i.horas.promedio, 0))}h
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
